/*
 * Self-serve trade account — form submit + modal.
 *
 * Posts the questionnaire to the app proxy at /apps/trade. Shopify signs that request and adds
 * logged_in_customer_id, so the backend can trust who is asking. Same-origin, so no CORS.
 *
 * Progressive enhancement throughout: with JS off, the price gate's "Unlock your pricing" link
 * is an ordinary <a> to the trade-account page, and the form is a normal <form>. Nothing here is
 * required to reach the questionnaire — it only makes it nicer.
 */
(function () {
  'use strict';

  var MODAL_ID = 'wf-trade-modal';

  function byAttr(root, attr) {
    return (root || document).querySelector('[' + attr + ']');
  }

  /* ------------------------------------------------------------------ submit */

  function serialise(form) {
    var data = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.disabled) return;
      if (el.type === 'checkbox') {
        data[el.name] = el.checked;
      } else if (el.type === 'radio') {
        if (el.checked) data[el.name] = el.value;
      } else {
        data[el.name] = el.value;
      }
    });
    return data;
  }

  function show(form, kind, message) {
    var box = byAttr(form, 'data-wf-trade-result');
    if (!box) return;
    box.hidden = false;
    box.className = 'wf-trade__result is-' + kind;
    box.textContent = message;
    if (kind !== 'working') box.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function setBusy(form, busy, label) {
    var btn = byAttr(form, 'data-wf-trade-submit');
    var lab = byAttr(form, 'data-wf-trade-btn-label');
    if (btn) btn.disabled = busy;
    if (lab && label) lab.textContent = label;
  }

  // Shared with the server, which applies the same rule: no letters, 7-15 digits.
  function validPhone(v) {
    var raw = String(v || '').trim();
    if (/[A-Za-z]/.test(raw)) return false;              // "Langley" and the like
    if (/[^0-9+()\-.\s]/.test(raw)) return false;        // stray symbols
    var digits = (raw.match(/\d/g) || []).length;
    return digits >= 7 && digits <= 15;                  // 15 = E.164 maximum
  }

  function firstInvalid(form) {
    var required = form.querySelectorAll('[required]');
    for (var i = 0; i < required.length; i++) {
      var el = required[i];
      if (!String(el.value || '').trim()) return el;
    }
    // A phone number is digits and punctuation, never letters. Shopify rejects companyCreate
    // outright for anything else ("Phone is invalid"), so catch it here where we can say why.
    var tel = form.querySelector('[name="phone"]');
    if (tel && !validPhone(tel.value)) return tel;
    return null;
  }

  function submit(form, ev) {
    ev.preventDefault();

    var missing = firstInvalid(form);
    if (missing) {
      show(form, 'error', missing.name === 'phone'
        ? 'Please check the phone number — numbers only, no letters.'
        : 'Please fill in the fields marked with an asterisk.');
      missing.focus();
      return;
    }

    var endpoint = form.getAttribute('data-wf-trade-endpoint') || '/apps/trade';
    setBusy(form, true, 'Setting up…');
    show(form, 'working', 'Setting up your account…');

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(serialise(form))
    })
      .then(function (r) {
        return r.json().catch(function () {
          throw new Error('We could not read the response from the server.');
        });
      })
      .then(function (res) {
        if (res && res.ok) {
          // Observed 2026-09-02: Shopify ENDS the session server-side the moment the customer
          // becomes a company contact — the buyer is signed out without doing anything. So send
          // them to the login page, not /account/logout: they are already logged out, and
          // logging out again would just be a confusing extra hop.
          show(form, 'success', res.message || 'Your trade account is set up.');
          setBusy(form, true, 'Done');
          var box = byAttr(form, 'data-wf-trade-result');
          if (box) {
            var actions = document.createElement('p');
            actions.className = 'wf-trade__result-actions';

            var signin = document.createElement('a');
            signin.className = 'wf-trade__btn';
            signin.href = '/account/login';
            signin.textContent = 'Sign back in to see my pricing';

            actions.appendChild(signin);
            box.appendChild(actions);

            if (res.requiresRelogin !== false) {
              var why = document.createElement('p');
              why.className = 'wf-trade__note';
              why.textContent =
                'Setting up a trade account signs you out automatically — that is Shopify, not ' +
                'a fault. Signing back in is usually a single click, and your pricing will be ' +
                'waiting.';
              box.appendChild(why);
            }
          }
          return;
        }

        var status = (res && res.status) || 'error';
        var msg = (res && res.message) || 'Something went wrong. Please try again.';
        if (status === 'not_logged_in') {
          show(form, 'error', 'Your session has expired. Please log in again.');
          setBusy(form, false, 'Turn on my pricing');
          window.setTimeout(function () { window.location.href = '/account/login'; }, 1500);
          return;
        }
        show(form, status === 'already_linked' ? 'success' : 'error', msg);
        setBusy(form, status === 'already_linked', 'Turn on my pricing');
      })
      .catch(function (err) {
        show(form, 'error',
          'We could not reach the server. Please try again, or contact us and we will set your ' +
          'account up manually.');
        setBusy(form, false, 'Turn on my pricing');
        if (window.console) console.error('[wf-trade]', err);
      });
  }

  /* ------------------------------------------------------------------- modal */

  function openModal(ev) {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;               // no modal on this page — let the link navigate
    if (ev) ev.preventDefault();
    modal.hidden = false;
    document.documentElement.classList.add('wf-trade-modal-open');
    var focusable = modal.querySelector('input, select, button');
    if (focusable) focusable.focus();
  }

  function closeModal() {
    var modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove('wf-trade-modal-open');
  }

  /* -------------------------------------------------------------------- init */

  function init() {
    var form = document.getElementById('wf-trade-form');
    if (form && !form.dataset.wfTradeBound) {
      form.dataset.wfTradeBound = '1';
      form.addEventListener('submit', function (ev) { submit(form, ev); });
    }

    document.addEventListener('click', function (ev) {
      var opener = ev.target.closest && ev.target.closest('[data-wf-trade-open]');
      if (opener) { openModal(ev); return; }
      var closer = ev.target.closest && ev.target.closest('[data-wf-trade-close]');
      if (closer) { ev.preventDefault(); closeModal(); }
    });

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') closeModal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
